import { onIdTokenChanged, signOut, type User } from 'firebase/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { auth } from '../firebase';

export type Role = 'staff' | 'admin';

export type AuthState =
  | { status: 'loading'; user: null; role: null }
  | { status: 'signedOut'; user: null; role: null }
  | { status: 'signedIn'; user: User; role: Role | null };

interface AuthContextValue {
  state: AuthState;
  /** Forces a token refresh so a role change applies without signing out. */
  refreshRole: () => Promise<void>;
  signOutUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function roleFromClaims(claims: Record<string, unknown>): Role | null {
  return claims.role === 'staff' || claims.role === 'admin' ? claims.role : null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading', user: null, role: null });

  // Fires on sign-in, sign-out and every ID token refresh, so role changes propagate.
  useEffect(
    () =>
      onIdTokenChanged(auth, async (user) => {
        if (!user) {
          setState({ status: 'signedOut', user: null, role: null });
          return;
        }
        const token = await user.getIdTokenResult();
        setState({ status: 'signedIn', user, role: roleFromClaims(token.claims) });
      }),
    [],
  );

  const refreshRole = useCallback(async () => {
    await auth.currentUser?.getIdToken(true);
  }, []);

  const signOutUser = useCallback(() => signOut(auth), []);

  const value = useMemo(() => ({ state, refreshRole, signOutUser }), [state, refreshRole, signOutUser]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}
